import React from 'react';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

// Shared bullet/range guardrail board (PRD OP_06 visual contract).
// Rows carry: metric, value, display, status, min, max, target, targetLabel,
// bands[{label,start,end,tone}], yearPoints[{year,value,display,status,position}],
// threshold, driver.

export const statusFor = (value, green, yellow, direction = 'higher') => {
  if (direction === 'lower') {
    if (value <= green) return 'green';
    if (value <= yellow) return 'yellow';
    return 'red';
  }
  if (value >= green) return 'green';
  if (value >= yellow) return 'yellow';
  return 'red';
};

export const toBulletPercent = (value, min, max) =>
  Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));

const bandWidth = (band, min, max) =>
  toBulletPercent(band.end, min, max) - toBulletPercent(band.start, min, max);

export const higherIsBetterBands = (min, yellow, target, max) => [
  { label: 'Breach', start: min, end: yellow, tone: 'red' },
  { label: 'Watch', start: yellow, end: target, tone: 'yellow' },
  { label: 'Healthy', start: target, end: max, tone: 'green' }
];

export const lowerIsBetterBands = (min, target, yellow, max) => [
  { label: 'Healthy', start: min, end: target, tone: 'green' },
  { label: 'Watch', start: target, end: yellow, tone: 'yellow' },
  { label: 'Breach', start: yellow, end: max, tone: 'red' }
];

const STATUS_ICON = { green: CheckCircle2, yellow: AlertTriangle, red: XCircle };
const STATUS_LABEL = { green: 'Green', yellow: 'Yellow', red: 'Red' };

const formatPct = (value, digits = 0) => `${Number(value).toFixed(digits)}%`;

const ForecastGuardrailBoard = ({ rows }) => (
  <>
    <div className="forecast-bullet-legend" aria-hidden="true">
      <span className="actual-key">Actual forecast</span>
      <span className="target-key">Management target</span>
      <span className="dot-key">FY year point</span>
      <span className="band-key green">Healthy</span>
      <span className="band-key yellow">Watch</span>
      <span className="band-key red">Breach</span>
    </div>

    <div className="forecast-constraint-continuum">
      {rows.map((row) => {
        const StatusIcon = STATUS_ICON[row.status];
        const actualPercent = toBulletPercent(row.value, row.min, row.max);
        const targetPercent = toBulletPercent(row.target, row.min, row.max);
        return (
          <div className={`forecast-bullet-row ${row.status}`} key={row.metric}>
            <div className="forecast-bullet-label">
              <span>{row.metric}</span>
              <strong>{row.display}</strong>
              <small>{row.driver}</small>
            </div>

            <div
              className="forecast-bullet-track"
              role="img"
              aria-label={`${row.metric}: ${row.display}; ${row.targetLabel}; ${row.threshold}.`}
            >
              <div className="forecast-bullet-bands" aria-hidden="true">
                {row.bands.map((band) => (
                  <span
                    className={`forecast-bullet-band ${band.tone}`}
                    key={`${row.metric}-${band.label}`}
                    style={{
                      left: `${toBulletPercent(band.start, row.min, row.max)}%`,
                      width: `${bandWidth(band, row.min, row.max)}%`
                    }}
                  >
                    {band.label}
                  </span>
                ))}
              </div>
              <span className="forecast-bullet-actual" style={{ width: `${actualPercent}%` }} aria-hidden="true" />
              <span className="forecast-bullet-target" style={{ left: `${targetPercent}%` }} aria-hidden="true" />
              {(row.yearPoints || []).map((point) => (
                <span
                  className={`forecast-bullet-year-dot ${point.status}`}
                  key={`${row.metric}-${point.year}`}
                  style={{ left: `${point.position}%` }}
                  title={`${point.year}: ${point.display}`}
                >
                  {String(point.year).slice(-2)}
                </span>
              ))}
              <div className="forecast-bullet-axis" aria-hidden="true">
                <span>{formatPct(row.min)}</span>
                <span className="forecast-bullet-target-label" style={{ left: `${targetPercent}%` }}>
                  {row.targetLabel}
                </span>
                <span>{formatPct(row.max)}</span>
              </div>
            </div>

            <div className="forecast-bullet-status">
              <span className={`forecast-status-pill ${row.status}`}>
                <StatusIcon size={13} />
                {STATUS_LABEL[row.status]}
              </span>
              <small>{row.threshold}</small>
            </div>
          </div>
        );
      })}
    </div>
  </>
);

export default ForecastGuardrailBoard;
